/*
 * @providesModule BankWithdrawalController
 *
 */

import ModalNavigationController, {ModalProps} from 'ModalNavigationController';
import React, {ReactElement} from 'react';
import {BankWithdrawalPopup} from './Modals/BankWithdrawalPopup';
import {MS_PER_MIN} from 'SKZConstantsImplementation';
import * as PaymentDataUtils from 'PaymentDataUtils';
import BankWithdrawalScreen from './BankWithdrawalScreen';
import NavigationManager from 'NavigationManager';
import {
  BANK_WITHDRAWAL_SUCCESS_MODAL_DEFAULTS,
  BANK_WITHDRAWAL_SUCCESS_MODAL_RTP_DEFAULTS,
  BankWithdrawalConfig,
  EBankWithdrawalOptions,
} from './BankWithdrawalConstants';
import {ServerPaymentsResponse} from 'StoreInfo';
import {_m} from 'INTL';
import {updateUserProperties} from 'UserPropertiesController';
import ClientPropertyConstants from 'ClientPropertyConstants';
import {AllUserPropertiesKeys} from 'UserPropertiesConstants';
import WithdrawalManager from 'WithdrawalManager';
import {
  WITHDRAWAL_LINK_BANK_POPUP_SHOWN,
  WITHDRAWAL_LINK_BANK_POPUP_CLICK,
  WITHDRAWAL_BANK_NAVIGATOR_OPTION_CLICK,
} from 'AWSConstants';
import BankWithdrawalSuccessModal from './Modals/BankWithdrawalSuccessModal';
import InterstitialsManager from 'InterstitialsManager';
import {Interstitials} from 'InterstitialConstants';
import GlobalEventEmitter from 'GlobalEventEmitter';
import {Navigator} from 'react-native-deprecated-custom-components';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';

export function displayBankWithdrawalScreen(options: BankWithdrawalConfig) {
  if (options.launchFromStandardWithdrawalPage) {
    NavigationManager.pushSceneWithHomeAsRoot({
      scene: 'BankWithdrawalScreen',
      render: (): ReactElement => {
        return (
          <BankWithdrawalScreen
            hasBackButton={true}
            onBackNavigation={(): void => {
              if (NavigationManager.isSceneInRoute('WithdrawalOptionScreen')) {
                WithdrawalManager.trackAWSWithdrawalEvent(
                  WITHDRAWAL_BANK_NAVIGATOR_OPTION_CLICK,
                  {}
                );
              }

              NavigationManager.pop();
            }}
            onWithdrawalSuccess={(
              withdrawalAmount: string,
              canPerformRTPWithdrawal: boolean,
              rtpEnabled: boolean
            ) => {
              NavigationManager.popToRoot();
              const delayToShowSuccessModal = 1000; // Delay to ensure the pop is completed before showing the modal
              setTimeout(() => {
                showBankWithdrawalSuccessModal(
                  withdrawalAmount,
                  canPerformRTPWithdrawal,
                  rtpEnabled
                );
              }, delayToShowSuccessModal);
            }}
            withdrawOption={options.withdrawalType}
            launchBankLinkOnMount={options.launchBankLinkOnMount}
            rtpEnabled={WithdrawalManager.isBankWithdrawalRTPEnabled()}
          />
        );
      },
      configure: (): Navigator.SceneConfigs => Navigator.SceneConfigs.HorizontalSwipeJump,
    });
    return;
  }
  NavigationManager.pushComponentWithScene(
    <BankWithdrawalScreen
      hasBackButton={true}
      onBackNavigation={(): void => {
        if (NavigationManager.isSceneInRoute('WithdrawalOptionScreen')) {
          WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_BANK_NAVIGATOR_OPTION_CLICK, {});
        }

        NavigationManager.pop();
      }}
      onWithdrawalSuccess={(
        withdrawalAmount: string,
        canPerformRTPWithdrawal: boolean,
        rtpEnabled: boolean
      ) => {
        NavigationManager.popToRoot();
        const delayToShowSuccessModal = 1000; // Delay to ensure the pop is completed before showing the modal
        setTimeout(() => {
          showBankWithdrawalSuccessModal(withdrawalAmount, canPerformRTPWithdrawal, rtpEnabled);
        }, delayToShowSuccessModal);
      }}
      withdrawOption={options.withdrawalType}
      launchBankLinkOnMount={options.launchBankLinkOnMount}
      rtpEnabled={WithdrawalManager.isBankWithdrawalRTPEnabled()}
    />,
    true,
    'BankWithdrawalScreen'
  );
}

export async function showBankWithdrawalPopup(
  hasLinkedBanks: boolean,
  isRTPEnabled: boolean,
  userPaymentData: ServerPaymentsResponse
) {
  const title = hasLinkedBanks
    ? _m('Get your winnings\nfast & easy!')
    : _m('Want your winnings\nfast & easy?');
  const description = hasLinkedBanks
    ? _m('Congrats! Use your bank for secure and direct payouts - usually within 24 hours.')
    : _m('Link your bank once to unlock secure, direct payouts - usually within 24 hours.');
  const ctaText = hasLinkedBanks ? _m('GO WITHDRAW!') : _m('LINK MY BANK');

  WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_LINK_BANK_POPUP_SHOWN, {});
  trackTelemetryEvent({
    event: TelemetryEventName.WITHDRAW_OPTIONS_SCREEN_POPUP_SHOW,
    includeUserWalletDetails: true,
  });
  ModalNavigationController.showModal(
    <BankWithdrawalPopup
      title={title}
      description={description}
      ctaText={ctaText}
      onPress={() => {
        ModalNavigationController.hideModal();
        WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_LINK_BANK_POPUP_CLICK, {
          CTA_ACTION: 'LINK_BANK',
        });
        displayBankWithdrawalScreen({
          withdrawalType: isRTPEnabled
            ? EBankWithdrawalOptions.BANK_INSTANT_WITHDRAWAL
            : EBankWithdrawalOptions.BANK_DEFAULT_WITHDRAWAL,
          launchBankLinkOnMount: hasLinkedBanks ? false : true,
        });
      }}
      onClose={() => {
        WithdrawalManager.trackAWSWithdrawalEvent(WITHDRAWAL_LINK_BANK_POPUP_CLICK, {
          CTA_ACTION: 'DISMISS',
        });
        ModalNavigationController.hideModal();
      }}
      isRTPEnabled={isRTPEnabled}
      userPaymentData={userPaymentData}
    />,
    {
      animationType: 'fade',
      transparent: true,
      modalBackHandler: () => ModalNavigationController.hideModal(),
    }
  );
}

/**
 * Can show the bank withdrawal popup if:
 * 1. First time visit
 * 2. After a first-time deposit
 * 3. For users that has no linked bank account: will show every 2 days if no bank account is linked
 */
export function canShowBankWithdrawalPopup(
  userPaymentData: ServerPaymentsResponse,
  withdrawalUserProperties: Record<AllUserPropertiesKeys, string>
): boolean {
  const lastTimeSeenBankPopup = withdrawalUserProperties['withdrawal.lastTimeSeenBankPopup'];
  const firstTimeDepositorHasSeenPopup =
    withdrawalUserProperties['withdrawal.firstTimeDepositorHasSeenBankPopup'];

  /** Client property check */
  if (!ClientPropertyConstants.getPlayerWithdrawalAeropayBankPopupEnabled()) {
    console.debug('BankWithdrawalController: canShowBankWithdrawalPopup: client property disabled');
    return false;
  }

  /** First time visit */
  const isFirstTimeVisit = !lastTimeSeenBankPopup;
  if (isFirstTimeVisit) {
    console.debug('BankWithdrawalController: canShowBankWithdrawalPopup: first time visit');
    return true;
  }

  /** After a first-time deposit */
  const isNewPayingUser = PaymentDataUtils.isNewPayingUser(userPaymentData);
  if (isNewPayingUser && !firstTimeDepositorHasSeenPopup) {
    console.debug('BankWithdrawalController: canShowBankWithdrawalPopup: is new paying user');
    updateUserProperties('withdrawal.firstTimeDepositorHasSeenBankPopup', JSON.stringify(true));
    return true;
  }

  /** For users that has no linked bank account: will show every 2 days if no bank account is linked */
  const currentDate = new Date();
  const lastSeenDate = isFirstTimeVisit ? currentDate : new Date(lastTimeSeenBankPopup);
  const popupIntervalMS =
    ClientPropertyConstants.getPlayerWithdrawalAeropayBankPopupIntervalMinutes() * MS_PER_MIN;
  const hasReachedIntervalToShowModal =
    currentDate.getTime() - lastSeenDate.getTime() > popupIntervalMS;

  if (!isFirstTimeVisit && hasReachedIntervalToShowModal) {
    console.debug('BankWithdrawalController: canShowBankWithdrawalPopup: can show after interval');
    return true;
  }

  return false;
}

function showBankWithdrawalSuccessModal(
  withdrawalAmount: string,
  canPerformRTPWithdrawal: boolean,
  rtpEnabled: boolean
) {
  const modalProps = {
    animationType: 'fade',
    transparent: true,
  } as ModalProps;

  const bodyText = canPerformRTPWithdrawal
    ? BANK_WITHDRAWAL_SUCCESS_MODAL_RTP_DEFAULTS.bodyText(withdrawalAmount)
    : BANK_WITHDRAWAL_SUCCESS_MODAL_DEFAULTS.bodyText(withdrawalAmount);

  const bankWithdrawalSuccessModal = (
    <BankWithdrawalSuccessModal
      bodyText={bodyText}
      onDismiss={() => {
        NavigationManager.popEverythingToRoot();
        GlobalEventEmitter.emit('switchToTab', 'PLAY');
        ModalNavigationController.hideModal(() =>
          InterstitialsManager.sendTrigger(Interstitials.POST_WITHDRAWAL)
        );
      }}
      isRTPTransaction={rtpEnabled && canPerformRTPWithdrawal}
    />
  );

  ModalNavigationController.showModal(bankWithdrawalSuccessModal, modalProps);
}
